import { useAuiEvent } from '@assistant-ui/react';
import { toast } from '@iq/ui';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface AttachmentAddErrorPayload {
  reason: 'no-adapter' | 'not-accepted' | 'adapter-error';
  message: string;
  attachmentId?: string;
}

/**
 * Subscribes to the composer's `attachmentAddError` event and routes
 * each error reason to a localized `sonner` toast. Renders nothing; must
 * be mounted exactly once inside the `AssistantRuntimeProvider` subtree
 * so the store can attach the listener.
 *
 * - `not-accepted` → the picked mime is not in the allowed set;
 *   shows a generic "unsupported file type" message.
 * - `adapter-error` → the adapter threw an `Error` whose `.message`
 *   already carries localized cap-violation copy; rendered verbatim.
 * - `no-adapter` → the runtime has no attachment adapter configured.
 */
export const ComposerAttachmentToast: FC = () => {
  const { t } = useTranslation();
  // Subscribes to the `composer.attachmentAddError` event by name.
  useAuiEvent('composer.attachmentAddError', (payload: AttachmentAddErrorPayload) => {
    if (payload.reason === 'not-accepted') {
      toast.error(t('chat.attachment.errors.unsupportedType', { name: '' }));
      return;
    }
    if (payload.reason === 'no-adapter') {
      toast.error(t('chat.attachment.errors.noAdapter'));
      return;
    }
    // adapter-error: the message was already localized inside the adapter.
    toast.error(payload.message || t('chat.attachment.errors.uploadFailed', { status: 'unknown' }));
  });
  return null;
};
